import { useEffect, useRef, useState } from "react";
import type { ExportJob } from "../lib/api.ts";
import { t, type Key } from "../lib/i18n.ts";
import { addElement, addScene, duplicateElement, elementLabel, newElement, removeElement, sceneAt, sceneOf, setKeyframe, resolvedValue, type NewKind } from "../lib/ops.ts";
import { browserEnv, EditorStore } from "../lib/store.ts";
import { Err, Loading, Modal, Spinner, msgOf, useApp, useLang } from "./common.tsx";
import { useStore } from "./hooks.ts";
import { Inspector } from "./inspector.tsx";
import { Stage } from "./stage.tsx";
import { CodeStage, isCodeProject } from "./codestage.tsx";
import { Timeline } from "./timeline.tsx";
import { SimpleEditor } from "./simple.tsx";
import { ImageLibrary } from "./images.tsx";
import { ComponentsPanel } from "./components.tsx";
import { ExportModal } from "./exportmodal.tsx";
import { SubtitlesPanel } from "./subtitles.tsx";
import { removeCue } from "../lib/subtitles.ts";
import { AudioCtx, AudioLibrary, useAudioState, usePlaybackSync } from "./audio.tsx";
import { removeClip } from "../lib/audio.ts";

const KIND_ICON: Record<string, string> = {
  text: "M5 6V4h14v2M12 4v16M9 20h6", rect: "M4 6h16v12H4z", ellipse: "M12 5c4.4 0 8 3.1 8 7s-3.6 7-8 7-8-3.1-8-7 3.6-7 8-7z", star: "M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z",
  ring: "M12 4a8 8 0 100 16 8 8 0 000-16zM12 9a3 3 0 100 6 3 3 0 000-6z", particles: "M6 7h.01M12 4h.01M18 8h.01M4 14h.01M10 12h.01M16 15h.01M8 19h.01M19 19h.01M13 20h.01",
  cube: "M12 3l8 4.5v9L12 21l-8-4.5v-9zM12 12l8-4.5M12 12v9M12 12L4 7.5", sphere: "M12 4a8 8 0 100 16 8 8 0 000-16zM4 12h16M12 4c2.5 2.2 2.5 13.8 0 16M12 4c-2.5 2.2-2.5 13.8 0 16",
  torus: "M12 6c5 0 9 2.7 9 6s-4 6-9 6-9-2.7-9-6 4-6 9-6zM12 10c2 0 4 .9 4 2s-2 2-4 2-4-.9-4-2 2-2 4-2z", cylinder: "M5 6c0-1.1 3.1-2 7-2s7 .9 7 2-3.1 2-7 2-7-.9-7-2zM5 6v12c0 1.1 3.1 2 7 2s7-.9 7-2V6",
  cone: "M12 4L5 18c0 1.1 3.1 2 7 2s7-.9 7-2z", camera: "M4 8h3l2-3h6l2 3h3v11H4zM12 11a3.5 3.5 0 100 7 3.5 3.5 0 000-7z",
};
const KINDS: [NewKind, string][] = [["text", "lib.text"], ["rect", "lib.rect"], ["ellipse", "lib.ellipse"], ["star", "lib.star"], ["ring", "lib.ring"], ["particles", "lib.particles"], ["cube", "lib.cube"], ["sphere", "lib.sphere"],
  ["torus", "lib.torus"], ["cylinder", "lib.cylinder"], ["cone", "lib.cone"], ["camera", "lib.camera"]];

export function Editor({ id }: { id: string }) {
  useLang();
  const { api, go } = useApp();
  const store = useRef(null as EditorStore | null);
  if (!store.current) store.current = new EditorStore(browserEnv(api));
  const s = store.current;
  const [err, setErr] = useState(null as string | null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true; setReady(false); setErr(null);
    api.getProject(id).then((p) => { if (alive) { s.load(p); { const f = [...p.scenes].sort((a, b) => a.start_ms - b.start_ms)[0]; if (f) s.seek(Math.min(f.start_ms + Math.round(f.duration_ms * 0.4), p.duration_ms)); } setReady(true); } }, (e) => { if (alive) setErr(msgOf(e)); });
    return () => { alive = false; };
  }, [id]);
  useEffect(() => () => s.dispose(), []);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (s.dirty || s.getState().save === "saving") { e.preventDefault(); e.returnValue = ""; } };
    addEventListener("beforeunload", h); return () => removeEventListener("beforeunload", h);
  }, []);
  if (err) return <div className="page"><Err>{err}</Err><button className="btn" onClick={() => go("/projects")}>{t("ed.back")}</button></div>;
  if (!ready) return <div className="page"><Loading /></div>;
  return <EditorBody store={s} />;
}

function EditorBody({ store }: { store: EditorStore }) {
  const { go } = useApp();
  const st = useStore(store), p = st.project!;
  const [exporting, setExporting] = useState(false);
  const [note, setNote] = useState(null as string | null);
  const [compact, setCompact] = useState(() => matchMedia("(max-width: 900px)").matches);
  const [mode, setMode] = useState((() => { try { return localStorage.getItem("modila.mode") === "simple" ? "simple" : "pro"; } catch { return "pro"; } })() as "simple" | "pro");
  useEffect(() => { const m = matchMedia("(max-width: 900px)"), f = () => setCompact(m.matches); m.addEventListener("change", f); return () => m.removeEventListener("change", f); }, []);
  const simple = !isCodeProject(p) && (compact || mode === "simple");
  const pick = (m: "simple" | "pro") => { setMode(m); try { localStorage.setItem("modila.mode", m); } catch { /* private mode */ } };
  const A = useAudioState();
  usePlaybackSync(store, A, p, st.playing, st.timeMs);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey, S = store.getState(), q = S.project!;
      if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? store.redo() : store.undo(); }
      else if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); store.redo(); }
      else if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); void store.saveNow(); }
      else if (mod && e.key.toLowerCase() === "d" && S.selection.kind === "element") {
        e.preventDefault(); const r = duplicateElement(q, S.selection.id); store.edit(() => r.project); store.select({ kind: "element", id: r.id });
      } else if (e.key === " " && !(el && el.tagName === "BUTTON")) { e.preventDefault(); S.playing ? store.pause() : store.play(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && S.selection.kind === "audio") { e.preventDefault(); const id = S.selection.id; store.edit((x) => removeClip(x, id)); store.select({ kind: "none" }); }
      else if ((e.key === "Delete" || e.key === "Backspace") && S.selection.kind === "cue") { e.preventDefault(); const id = S.selection.id; store.edit((x) => removeCue(x, id)); store.select({ kind: "none" }); }
      else if ((e.key === "Delete" || e.key === "Backspace") && S.selection.kind === "element") { e.preventDefault(); const id = S.selection.id; store.edit((x) => removeElement(x, id)); store.select({ kind: "none" }); }
      else if (e.key.startsWith("Arrow") && S.selection.kind === "element" && q.elements[S.selection.id]?.type !== "camera") {
        e.preventDefault(); const d = e.shiftKey ? 10 : 1, id = S.selection.id;
        const dx = e.key === "ArrowLeft" ? -d : e.key === "ArrowRight" ? d : 0, dy = e.key === "ArrowUp" ? -d : e.key === "ArrowDown" ? d : 0;
        store.edit((x) => {
          const sc = sceneOf(x, id); if (!sc) return x;
          let n = x;
          for (const [prop, dv] of [["x", dx], ["y", dy]] as const) if (dv) n = setPV(n, id, prop, resolvedValue(n, id, prop, S.timeMs) + dv, S.timeMs);
          return n;
        }, `key:${id}`);
      }
    };
    addEventListener("keydown", onKey); return () => removeEventListener("keydown", onKey);
  }, [store]);

  const activeScene = () => {
    const sel = store.getState().selection;
    if (sel.kind === "scene") return sel.id;
    if (sel.kind === "element") return sceneOf(store.getState().project!, sel.id)?.id;
    return sceneAt(store.getState().project!, store.getState().timeMs)?.id ?? p.scenes[p.scenes.length - 1]?.id;
  };
  const add = (kind: NewKind) => {
    const sid = activeScene();
    if (!sid) { setNote(t("lib.no_scene")); return; }
    const sc = p.scenes.find((x) => x.id === sid)!;
    if (kind === "camera" && sc.element_ids.some((e) => p.elements[e]?.type === "camera")) { setNote(t("lib.one_camera")); return; }
    setNote(null);
    const el = newElement(kind, p);
    store.edit((q) => addElement(q, sid, el)); store.select({ kind: "element", id: el.id });
  };
  const addSc = () => { const r = addScene(p); if (!r.sceneId) return; store.edit(() => r.project); store.select({ kind: "scene", id: r.sceneId }); store.seek(p.scenes.length ? r.project.scenes.find((x) => x.id === r.sceneId)!.start_ms : 0); };
  const rename = (name: string) => store.edit((q) => ({ ...q, name }), "name");

  const saveKey = ("save." + st.save) as Key;
  if (simple) return (
    <AudioCtx.Provider value={A}>
      <div className="editor-simple">
        <header className="sp-bar">
          <button className="btn sm ghost" onClick={() => go("/projects")} aria-label={t("ed.back")}>←<span className="hide-s"> {t("ed.back")}</span></button>
          <input className="input name" aria-label={t("new.name")} maxLength={120} value={p.name} placeholder={t("ed.untitled")} onChange={(e: any) => rename(e.target.value)} />
          <span className="savestate" data-s={st.save} role="status"><i /><span className="hide-s">{t(saveKey)}</span></span>
          {!compact && <button className="btn sm" onClick={() => pick("pro")}>{t("simple.pro")}</button>}
          <button className="btn sm primary" disabled={p.scenes.length === 0} onClick={() => setExporting(true)}>{t("ed.export")}</button>
        </header>
        <SimpleEditor store={store} />
        {exporting && <ExportModal store={store} onClose={() => setExporting(false)} />}
      </div>
    </AudioCtx.Provider>
  );
  return (
    <AudioCtx.Provider value={A}>
    <div className="editor">
      <header className="ed-top">
        <button className="btn sm ghost" onClick={() => go("/projects")}>← {t("ed.back")}</button>
        <input className="input name" aria-label={t("new.name")} maxLength={120} value={p.name} placeholder={t("ed.untitled")} onChange={(e: any) => rename(e.target.value)} />
        <span className="savestate" data-s={st.save} role="status"><i />{t(saveKey)}</span>
        {st.save === "conflict" && <button className="btn sm" onClick={() => location.reload()}>{t("save.reload")}</button>}
        {st.save === "error" && <span className="muted" style={{ fontSize: 12 }}>{st.saveMessage}</span>}
        <span className="sp" />
        <button className="btn sm icon" disabled={!st.canUndo} onClick={() => store.undo()} aria-label={t("ed.undo")} title={t("ed.undo")}>↶</button>
        <button className="btn sm icon" disabled={!st.canRedo} onClick={() => store.redo()} aria-label={t("ed.redo")} title={t("ed.redo")}>↷</button>
        <button className="btn sm" onClick={() => pick("simple")}>{t("simple.simple")}</button>
        <button className="btn sm primary" disabled={p.scenes.length === 0} onClick={() => setExporting(true)}>{t("ed.export")}</button>
      </header>

      {isCodeProject(p) ? <aside className="panel ed-lib" aria-label={t("lib.layers")}><div><h3 className="h">{t("lib.layers")}</h3><p className="muted">{t("code.hint")}</p></div></aside> : <aside className="panel ed-lib" aria-label={t("lib.elements")}>
        <div><h3 className="h">{t("lib.elements")}</h3>
          <div className="tools">{KINDS.map(([k, lk]) => <button key={k} className="btn sm tool" onClick={() => add(k)}><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d={KIND_ICON[k] ?? "M5 12h14"} /></svg><span>{t(lk as Key)}</span></button>)}
          </div>
          <div style={{ marginTop: 8 }}><button className="btn sm" style={{ width: "100%" }} onClick={addSc}>＋ {t("lib.scene")}</button></div>
          {note && <div className="alert warn" style={{ marginTop: 8 }} role="status">{note}</div>}
        </div>
        <ComponentsPanel store={store} projectId={p.id} />
        <ImageLibrary store={store} />
        <AudioLibrary store={store} />
        <SubtitlesPanel store={store} p={p} />
        <div><h3 className="h">{t("lib.layers")}</h3>
          <div className="layers" role="listbox">
            {[...p.scenes].sort((a, b) => a.start_ms - b.start_ms).map((sc) => (
              <div key={sc.id}>
                <button className="layer" role="option" aria-selected={st.selection.kind === "scene" && st.selection.id === sc.id}
                  onClick={() => { store.select({ kind: "scene", id: sc.id }); store.seek(sc.start_ms); }}><strong>{sc.name || t("insp.scene")}</strong></button>
                {sc.element_ids.map((eid) => p.elements[eid] && (
                  <button key={eid} className="layer" style={{ paddingLeft: 18 }} role="option" aria-selected={st.selection.kind === "element" && st.selection.id === eid}
                    onClick={() => store.select({ kind: "element", id: eid })}>{elementLabel(p.elements[eid]!)} <small>{p.elements[eid]!.type}</small></button>))}
              </div>))}
          </div>
        </div>
      </aside>}

      {isCodeProject(p) ? <CodeStage store={store} /> : <Stage store={store} />}
      <Inspector store={store} />
      <Timeline store={store} />
      {exporting && <ExportModal store={store} onClose={() => setExporting(false)} />}
    </div>
    </AudioCtx.Provider>
  );
}

// small local alias to keep the arrow-key handler tidy
import { setPropertyAtTime as setPV } from "../lib/ops.ts";
void setKeyframe;

